/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import { compose, withHandlers, withState } from 'recompose'
import { connect } from 'react-redux'
import { reduxForm } from 'redux-form'
import { withRouter } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import { ERROR_CODES } from 'constants/errors'
import { ALERTS } from 'constants/alerts'
import { withLocalization } from 'hocs/localization'
import { onResetPassword } from 'redux-app/modules/auth'
import { onShowError, createAlert } from 'redux-app/modules/alerts'
import { resendForgotPassword } from 'api/client/actions/auth'
import { isCaptchaEnabled, isCaptchaWhiteListed } from 'redux-app/selectors/apiData'
import { getErrorCodes } from 'utils/error'
import { getQueryFromSearch } from 'utils/location'
import { STEPS } from './constants'
import ChangePasswordForm from './ChangePasswordForm'

interface IValues {
  questionAnswer: string,
  password: string,
  confirmPassword: string,
}

export default compose(
  connect(
    state => ({
      isCaptchaEnabled: isCaptchaEnabled(state),
      isCaptchaWhiteListed: isCaptchaWhiteListed(state),
    }),
    {
      onResetPassword,
      onShowError,
      resendForgotPassword,
      createAlert,
    },
  ),
  withLocalization('auth.changePassword'),
  withRouter,
  withState('step', 'setStep', null),
  withState('isSent', 'onUpdateSend', false),
  withHandlers({
    onSubmit: ({
      onResetPassword,
      setStep,
      onShowError,
      createAlert,
      location: { search },
      history: { replace },
    }) => formValues => {
      const { token } = getQueryFromSearch(search)
      const { password } = formValues

      const requestData: any = {
        token,
        password,
      }

      return onResetPassword(requestData).then(() => {
        createAlert({ name: ALERTS.successChangePassword })
        replace(ROUTES.home.logIn)
      }).catch((error: any) => {
        if (error.status === 400 && getErrorCodes(error).includes(ERROR_CODES.linkExpired)) {
          return setStep(STEPS.expired)
        }
        if (error.status === 400 && getErrorCodes(error).includes(ERROR_CODES.linkUsed)) {
          return setStep(STEPS.used)
        }
        if (error.status === 400 && getErrorCodes(error).includes(ERROR_CODES.passwordUsedBefore)) {
          createAlert({ name: ALERTS.passwordUsedBefore })
          return
        }
        onShowError(error, requestData)
      })
    },
    onResendForgotPassword: ({
      resendForgotPassword,
      onShowError,
      location: { search },
      onUpdateSend,
    }) => () => {
      const { token } = getQueryFromSearch(search)

      return resendForgotPassword(token)
        .then(() => {
          onUpdateSend(true)
        })
        .catch(error => {
          onShowError(error)
        })
    },
  }),
  reduxForm({
    form: 'resetPassword',
    validate: (values: IValues, { getTextPath }) => {
      const errors: any = {}
      if (!values.password || values.password !== values.confirmPassword) {
        errors.confirmPassword = [getTextPath('errors.confirmPassword')]
      }
      return errors
    },
  }),
)(ChangePasswordForm)
